import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { EFFECTIVE_DATE, COMPANY_NAME, COMPANY_LEGAL, CONTACT_EMAIL } from '@/lib/constants';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    body: `By accessing or using the custum.ai website and any services provided by ${COMPANY_LEGAL} ("we", "us", or "our"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms in their entirety, you must not use our website or services.

These Terms constitute a legally binding agreement between you and ${COMPANY_LEGAL}. We reserve the right to modify these Terms at any time. We will provide notice of material changes by updating the effective date above. Continued use of our services after any such changes constitutes your acceptance of the revised Terms.`,
  },
  {
    id: 'services',
    title: '2. Services',
    body: `${COMPANY_NAME} provides AI-powered business solutions including, but not limited to:

• AI Caller — automated inbound and outbound voice agent services
• AI Receptionist — virtual receptionist and appointment booking services
• AI Webchat — website chatbot and lead capture services
• Custom Dashboard — analytics and performance reporting tools
• CRM integration and workflow automation services

All services are provided on a managed basis. We reserve the right to modify, suspend, or discontinue any service at any time with reasonable notice to active clients. Service specifications, including uptime commitments and response-time targets, are defined in any applicable Service Level Agreement ("SLA") agreed separately in writing.`,
  },
  {
    id: 'eligibility',
    title: '3. Eligibility and Account Registration',
    body: `Our services are intended for business use only. By using our services you represent that:

• You are at least 18 years of age
• You are using the services on behalf of a legitimate business entity
• You have the authority to bind that entity to these Terms
• All information you provide to us is accurate, current, and complete

You are responsible for maintaining the confidentiality of any account credentials and for all activity that occurs under your account. You must notify us immediately at ${CONTACT_EMAIL} if you suspect any unauthorised use of your account.`,
  },
  {
    id: 'ip',
    title: '4. Intellectual Property',
    body: `All content on the ${COMPANY_NAME} website — including text, graphics, logos, icons, images, audio clips, software, and the compilation thereof — is the property of ${COMPANY_LEGAL} or its content suppliers and is protected by applicable intellectual property laws.

AI agents, workflows, and automation systems built and trained by us using your business data remain the intellectual property of ${COMPANY_LEGAL}. You retain full ownership of your underlying business data, knowledge base content, and any materials you provide to us for the purpose of training.

You may not reproduce, distribute, modify, create derivative works of, publicly display, or commercially exploit any content from our website without our prior written consent.`,
  },
  {
    id: 'data',
    title: '5. Data and Privacy',
    body: `Your use of our services is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our services, you consent to the collection and use of information as described in our Privacy Policy.

You warrant that any data you provide to us for the purposes of training AI models or populating knowledge bases has been obtained lawfully, that you have all necessary rights to share it with us, and that its use by us as contemplated by these Terms will not infringe the rights of any third party.

All personal data is processed in accordance with applicable data protection legislation. We do not use client business data to train general-purpose AI models or share it with third parties except as necessary to provide the agreed services.`,
  },
  {
    id: 'prohibited',
    title: '6. Prohibited Uses',
    body: `You agree not to use our services to:

• Violate any applicable law or regulation
• Transmit any content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable
• Impersonate any person or entity or misrepresent your affiliation
• Attempt to gain unauthorised access to our systems or another user's account
• Introduce malware, viruses, or any other malicious code
• Use the services in a manner that could damage, overload, or impair our infrastructure
• Collect or harvest any personally identifiable information without explicit consent
• Use our AI agents to deceive end users in a way that causes harm

We reserve the right to terminate or suspend access to our services immediately, without notice, for any conduct we determine violates these Terms or is otherwise harmful.`,
  },
  {
    id: 'payment',
    title: '7. Fees and Payment',
    body: `Pricing for our services is custom-quoted based on your specific requirements. All pricing is agreed in writing prior to service commencement. Unless otherwise stated in your agreement:

• Fees are invoiced monthly in advance
• Payment is due within 30 days of invoice date
• All fees are exclusive of VAT or applicable taxes, which will be added where required
• Late payments may incur interest at 8% above the Bank of England base rate per annum
• We reserve the right to suspend services for accounts more than 30 days overdue

We will provide no less than 30 days' written notice before implementing any fee changes for existing clients.`,
  },
  {
    id: 'disclaimer',
    title: '8. Disclaimer of Warranties',
    body: `Our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.

We do not warrant that:

• Our services will meet your specific requirements or expectations
• AI-generated responses will be error-free, complete, or appropriate in every situation
• The services will be available without interruption at all times
• Results obtained from using our services will be accurate or reliable

AI systems by their nature may produce outputs that are imperfect or unexpected. Human oversight of AI agent interactions is recommended for high-stakes use cases.`,
  },
  {
    id: 'liability',
    title: '9. Limitation of Liability',
    body: `To the fullest extent permitted by applicable law, ${COMPANY_LEGAL} shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, goodwill, or business opportunities — arising from your use of or inability to use our services.

Our total cumulative liability to you for all claims arising under these Terms shall not exceed the total fees paid by you to us in the three months immediately preceding the event giving rise to the claim.

Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded under applicable law.`,
  },
  {
    id: 'termination',
    title: '10. Term and Termination',
    body: `These Terms remain in effect for as long as you use our services. Either party may terminate a service agreement by providing 30 days' written notice unless otherwise specified in an individual service contract.

We may terminate your access immediately and without notice if you breach these Terms, fail to pay fees when due, or engage in conduct that we reasonably believe poses a risk to us, our systems, or other clients.

Upon termination, your right to access the services ceases immediately. We will retain your data for 30 days following termination to allow for data export requests, after which it will be securely deleted in accordance with our data retention policy.`,
  },
  {
    id: 'governing',
    title: '11. Governing Law and Disputes',
    body: `These Terms are governed by and construed in accordance with the laws of England and Wales. Any dispute arising out of or in connection with these Terms, including any question regarding its existence, validity, or termination, shall be subject to the exclusive jurisdiction of the courts of England and Wales.

Before initiating any formal proceedings, both parties agree to attempt to resolve any dispute through good-faith negotiation for a period of at least 30 days following written notice of the dispute.`,
  },
  {
    id: 'general',
    title: '12. General Provisions',
    body: `Entire Agreement: These Terms, together with our Privacy Policy and any applicable service agreement or SLA, constitute the entire agreement between you and ${COMPANY_LEGAL} relating to your use of our services.

Severability: If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.

No Waiver: Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.

Assignment: You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations freely.

Contact: For any questions regarding these Terms, please contact us at ${CONTACT_EMAIL}.`,
  },
];

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="pt-24 min-h-screen bg-brand-black">
      <Helmet>
        <title>Terms & Conditions — custum.ai</title>
        <meta name="description" content="Terms and Conditions for custum.ai AI services including AI Caller, AI Receptionist, and AI Webchat." />
        <link rel="canonical" href="https://custum.ai/terms/" />
      </Helmet>

      {/* Header */}
      <section className="py-16 md:py-24 bg-brand-charcoal" style={{ borderBottom: '1px solid var(--brand-border)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
              Legal
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight mb-4">
              Terms &amp; Conditions
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-sm text-brand-muted">
              Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY_LEGAL}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

            {/* Sticky TOC */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-32">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-muted mb-4">Contents</p>
                <nav className="space-y-2">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-xs text-brand-muted hover:text-brand-white transition-colors leading-relaxed"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Body */}
            <div className="lg:col-span-3 space-y-12">
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-sm text-brand-muted leading-relaxed"
              >
                Please read these Terms and Conditions carefully before using our services. These Terms apply to all visitors, users, and clients of custum.ai.
              </motion.p>

              {sections.map((section, idx) => (
                <motion.div
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: idx * 0.03 }}
                >
                  <h2 className="text-lg font-bold text-brand-white mb-4 pb-3" style={{ borderBottom: '1px solid var(--brand-border)' }}>
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.body.split('\n\n').map((para, i) => (
                      <p key={i} className="text-sm text-brand-muted leading-relaxed whitespace-pre-line">
                        {para}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}

              <div className="pt-8 mt-8" style={{ borderTop: '1px solid var(--brand-border)' }}>
                <p className="text-xs text-brand-muted">
                  Questions about these Terms? Contact us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-blue-light hover:text-brand-white transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
